import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiBookmark, FiTrash2, FiPlay } from 'react-icons/fi';
import { useMovieContext } from '../context/MovieContext';
import { IMG_BASE } from '../api/tmdb';
import Footer from '../components/Footer';

const WatchlistPage = () => {
  const { watchlist, removeFromWatchlist } = useMovieContext();
  const navigate = useNavigate();

  return (
    <div style={{ background: 'var(--dark)', minHeight: '100vh', paddingTop: 88 }}>
      <div className="max-w-screen-xl mx-auto px-6 md:px-12">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <div className="flex items-center gap-3 mb-2">
            <FiBookmark size={28} style={{ color: 'var(--red)' }} />
            <h1 className="font-display text-4xl md:text-5xl tracking-wide" style={{ color: 'var(--text)' }}>
              My Watchlist
            </h1>
          </div>
          <p className="font-body text-sm" style={{ color: 'var(--muted)' }}>
            {watchlist.length} {watchlist.length === 1 ? 'movie' : 'movies'} saved
          </p>
        </motion.div>

        {watchlist.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center py-32 gap-6"
          >
            <div className="w-24 h-24 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(229,9,20,0.1)', border: '2px dashed rgba(229,9,20,0.3)' }}>
              <FiBookmark size={36} style={{ color: 'var(--red)' }} />
            </div>
            <div className="text-center">
              <h3 className="font-display text-2xl mb-2" style={{ color: 'var(--text)' }}>Nothing saved yet</h3>
              <p className="font-body text-sm" style={{ color: 'var(--muted)' }}>
                Add movies to your watchlist by clicking the + button
              </p>
            </div>
            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => navigate('/')}
              className="px-8 py-3 rounded-full font-body font-semibold text-sm"
              style={{ background: 'var(--red)', color: '#fff', boxShadow: '0 0 20px rgba(229,9,20,0.3)' }}
            >
              Browse Movies
            </motion.button>
          </motion.div>
        ) : (
          <div className="grid gap-4">
            <AnimatePresence>
              {watchlist.map((movie, i) => (
                <motion.div
                  key={movie.id}
                  layout
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20, height: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-center gap-4 rounded-2xl p-4 cursor-pointer group"
                  style={{
                    background: 'rgba(17,17,24,0.7)',
                    border: '1px solid rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(12px)',
                  }}
                  onClick={() => navigate('/movie/' + movie.id)}
                  whileHover={{ borderColor: 'rgba(229,9,20,0.3)', x: 4 }}
                >
                  {/* Poster */}
                  <div className="flex-shrink-0 rounded-xl overflow-hidden" style={{ width: 60, height: 88 }}>
                    {movie.poster_path
                      ? <img src={IMG_BASE + '/w92' + movie.poster_path} alt={movie.title} className="w-full h-full object-cover" />
                      : <div className="w-full h-full glass" />
                    }
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display text-xl leading-tight truncate mb-1" style={{ color: 'var(--text)' }}>
                      {movie.title}
                    </h3>
                    <p className="text-xs font-body line-clamp-2 mb-2" style={{ color: 'var(--muted)' }}>
                      {movie.overview}
                    </p>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-accent" style={{ color: 'var(--gold)' }}>
                        ★ {movie.vote_average?.toFixed(1)}
                      </span>
                      <span className="text-xs" style={{ color: 'var(--muted)' }}>
                        {movie.release_date?.split('-')[0]}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={e => { e.stopPropagation(); navigate('/movie/' + movie.id); }}
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ background: 'var(--red)', color: '#fff' }}
                    >
                      <FiPlay size={14} fill="currentColor" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1, color: '#E50914' }}
                      whileTap={{ scale: 0.9 }}
                      onClick={e => { e.stopPropagation(); removeFromWatchlist(movie.id); }}
                      className="w-10 h-10 rounded-full glass flex items-center justify-center transition-colors"
                      style={{ color: 'var(--muted)' }}
                    >
                      <FiTrash2 size={14} />
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default WatchlistPage;
